import React, { useEffect, useMemo, useState } from "react";
import TabPanel from "./TabPanel";
import { Tab, Tabs, IconButton } from "@mui/material";
import { colors, theme } from "theme";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import GradeView from "./GradeView";
import { Ptag } from "components/core";
import "./index.css";

const CustomTabs = ({
  grades,
  allowGradeAddition,
  handleGradeAddition,
  handleGradeDeletion,
  attributes,
  isFlat,
  defaultIndex,
  isPostIssuance,
  refreshGradesData,
}) => {
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    // Prevent selecting the "Add New Grade" tab (value = -1)
    if (newValue !== -1) {
      setValue(newValue);
    }
  };
  useEffect(() => {
    if (defaultIndex) {
      const index = grades.findIndex(
        (obj) => obj?.plan_grade_mapping_id === defaultIndex
      );
      setValue(index);
    }
  }, []);

  const interstitialGradeAddition = (value) => {
    if (handleGradeAddition) {
      if (Object.keys(value)?.length) {
        value.duplicate_data = true;
      }
      handleGradeAddition(value);
      setValue((prev) => prev + 1);
    }
  };

  const handleGradeDelete = (index) => {
    if (handleGradeDeletion) {
      handleGradeDeletion(index);
      
      // Adjust the selected tab after deletion
      if (index < value) {
        // If we deleted a grade before the current one, shift down
        setValue((prev) => prev - 1);
      } else if (index === value && value > 0) {
        // If we deleted the current grade, go to the previous one
        setValue((prev) => prev - 1);
      } else if (index === value && value === 0 && grades.length > 1) {
        // If we deleted the first grade and there are more grades, stay on first
        setValue(0);
      }
    }
  };
  return (
    <>
      {isFlat ? (
        <GradeView
          attributes={attributes}
          grade={grades[0]}
          isFlat={isFlat}
          isPostIssuance={isPostIssuance}
          refreshGradesData={refreshGradesData}
        />
      ) : (
        <>
          <div style={{ 
            display: "flex", 
            alignItems: "flex-end", 
            width: "100%",
            gap: "4px"
          }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: {
                  display: "none",
                  marginBottom: "-2px",
                  "& .Mui-selected": {
                    fontWeight: "bold",
                    backgroundColor: "transparent",
                  },
                },
              }}
              sx={{
                flex: 1,
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
                "& .MuiTabs-flexContainer": {
                  flexWrap: "nowrap",
                },
              }}
            >
              {grades.map((grade, index) => (
                <Tab
                  key={grade?.plan_grade_mapping_id || index}
                  sx={{
                    fontSize: "14px !important",
                    textTransform: "capitalize",
                    color: colors.primaryColor,
                    backgroundColor: "#231A3D1A",
                    borderRadius: "10px 10px 0px 0px",
                    margin: "0 2px",
                    minWidth: "120px",
                    flexShrink: 0,
                    position: "relative",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    boxShadow: "none",
                  }}
                  value={index}
                  label={
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "space-between",
                      width: "100%",
                      paddingRight: "8px"
                    }}>
                      <Ptag
                        color={colors.primaryColor}
                        fontSize="14px"
                        fontWeight="bold"
                      >
                        Grade {index + 1}
                      </Ptag>
                      {grades.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGradeDelete(index);
                          }}
                          sx={{
                            color: colors.primaryColor,
                          marginRight:'-18px',
                            "&:hover": {
                              backgroundColor: "rgba(35, 26, 61, 0.1)",
                            },
                          }}
                        >
                          <CancelOutlinedIcon fontSize="medium" />
                        </IconButton>
                      )}
                    </div>
                  }
                />
              ))}
              {allowGradeAddition && (
                <Tab
                  sx={{
                    fontSize: "12px !important",
                    textTransform: "capitalize",
                    color: theme.primaryColor,
                    backgroundColor: "#231A3D1A",
                    borderRadius: "10px 10px 0px 0px",
                    margin: "0 2px",
                    minWidth: "140px",
                    flexShrink: 0,
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    boxShadow: "none",
                    cursor: "pointer",
                  }}
                  value={-1}
                  label={
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      width: "100%"
                    }}>
                      <ControlPointIcon style={{ marginRight: "4px", fontSize: "16px" }} />
                      <Ptag
                        color={theme.primaryColor}
                        fontSize="12px"
                        fontWeight="bold"
                      >
                        Add New Grade
                      </Ptag>
                    </div>
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    interstitialGradeAddition({});
                  }}
                />
              )}
            </Tabs>
          </div>
          {grades.map((grade, index) => (
            <TabPanel
              value={value}
              index={index}
              key={grade?.plan_grade_mapping_id || index}
            >
              {attributes && (
                <GradeView
                  attributes={attributes}
                  grade={grade}
                  interstitialGradeAddition={interstitialGradeAddition}
                  isPostIssuance={isPostIssuance}
                  refreshGradesData={refreshGradesData}
                />
              )}
            </TabPanel>
          ))}
        </>
      )}
    </>
  );
};

export default CustomTabs;
